<template>
    <app-layout title="Beheer">
        <template #header>
            <page-title title="Webshop producten" />
        </template>

        <template #pageContent>
            <p>Alle producten die via de webshops kunnen worden besteld, moeten ook hier voorzien zijn om aanvragen correct in de Ecca+ tool te kunnen binnentrekken.<br />
                De prijzen hier zijn enkel nodig voor de prijsberekening, wanneer er een order vanuit een koppeling met een partner binnenkomt, bv. CIB of Whise.</p>

            <div class="flex justify-between mt-4">
                <TableSearch tableId="webshop-products" :values="webshopProductsGrouped" placeholder="Zoeken"/>

                <Modal buttonText="Toevoegen" buttonClass="add" @modalClosed="modalClosed" ref="modal" :visible="(this.$page.props.user.roles.includes('admin')) ? 'all' : 'hidden'">
                    <h2>Webshop product {{ modalTitleAction }}</h2>
                    <Form @submitted="handleSubmit" class="w-full">
                        <Select
                            label="Categorie"
                            name="assignment_type_id"
                            :required="true"
                            :options="assignmentTypes"
                            v-model="form.assignment_type_id"
                            :error="form.errors.assignment_type_id"
                            :disabled="!this.$page.props.user.roles.includes('admin') || modalTitleAction === 'bewerken'"
                        />
                        <Select
                            label="Webshop/partner"
                            name="webshop_id"
                            :required="true"
                            :options="webshops"
                            v-model="form.webshop_id"
                            :error="form.errors.webshop_id"
                            :disabled="!this.$page.props.user.roles.includes('admin') || modalTitleAction === 'bewerken'"
                        />
                        <Input
                            label="Prijs"
                            type="number"
                            name="price"
                            min="0"
                            :step="0.01"
                            v-model="form.price"
                            :error="form.errors.price"
                        />

                        <div v-if="this.$page.props.user.roles.includes('admin')">
                            <InputGroup layoutItems="horizontal">
                                <Input
                                    label="Geen webshop factuur"
                                    name="no_webshop_invoice"
                                    id="no_webshop_invoice"
                                    type="checkbox"
                                    :checked="Boolean(form.no_webshop_invoice)"
                                    value="1"
                                    v-model="form.no_webshop_invoice"
                                    layout="vertical"
                                />
                            </InputGroup>

                            <h3 class="mt-2">Product ids</h3>
                            <Input type="number" min="0" label="Product id NL" name="product_id_nl" :required="true" v-model="form.product_id_nl" :error="form.errors.product_id_nl" />
                            <Input type="number" min="0" label="Product id FR" name="product_id_fr" :required="true" v-model="form.product_id_fr" :error="form.errors.product_id_fr" />
                            <Input type="number" min="0" label="Product id EN" name="product_id_en" :required="true" v-model="form.product_id_en" :error="form.errors.product_id_en" />
                        </div>

                        <div class="inline-block self-end mt-4">
                            <Button text="Annuleren" buttonClass="secondary" data-modal="close" />
                            <Button
                                :text="modalTitleAction"
                                buttonType="submit"
                                buttonClass="save"
                                class="capitalize"
                                :loading="form.processing"
                            />
                        </div>
                    </Form>
                </Modal>
            </div>

            <Table id="webshop-products" :values="webshopProductsGrouped" class="mb-8">
                <thead>
                    <tr>
                        <Th :sort="false" :colspan="3"></Th>
                        <Th :sort="false" :colspan="3">Webshop product id</Th>
                        <Th :sort="false" :colspan="2"></Th>
                    </tr>
                    <tr>
                        <Th id="assignment_type.name">Product</Th>
                        <Th id="webshop.code">Webshop/partner</Th>
                        <Th id="no_webshop_invoice">Geen webshop factuur</Th>
                        <Th :sort="false">NL</Th>
                        <Th :sort="false">FR</Th>
                        <Th :sort="false">EN</Th>
                        <Th id="price" class="w-[150px]">Prijs</Th>
                        <Th :sort="false"></Th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(webshopProduct, key) in state.sortedValues['webshop-products']" :key="key">
                        <Td>
                            {{ webshopProduct.assignment_type.name }}
                        </Td>
                        <Td>
                            {{ webshopProduct.webshop.code.charAt(0).toUpperCase() + webshopProduct.webshop.code.slice(1) }}
                        </Td>
                        <Td class="text-center">
                            <i class="fas fa-check text-blue-dark-500" v-if="webshopProduct.no_webshop_invoice"></i>
                        </Td>
                        <Td>
                            <template v-for="(item, key) in webshopProducts" :key="key">
                                <template v-if="matchesSearch(item, webshopProduct, 'nl')">
                                    {{ item.product_id }}
                                </template>
                            </template>
                        </Td>
                        <Td>
                            <template v-for="(item, key) in webshopProducts" :key="key">
                                <template v-if="matchesSearch(item, webshopProduct, 'fr')">
                                    {{ item.product_id }}
                                </template>
                            </template>
                        </Td>
                        <Td>
                            <template v-for="(item, key) in webshopProducts" :key="key">
                                <template v-if="matchesSearch(item, webshopProduct, 'en')">
                                    {{ item.product_id }}
                                </template>
                            </template>
                        </Td>
                        <Td>
                            <div class="flex">
                                <span class="flex items-center mb-2 mr-1">&euro;</span>
                                {{ webshopProduct.price }}
                            </div>
                        </Td>
                        <Td>
                            <Button buttonClass="edit" size="small" title="Bewerken" @click="updateWebshopProduct(webshopProduct)" />
                            <Button buttonClass="delete" size="small" title="Verwijderen" @click="deleteWebshopProduct(webshopProduct)" v-if="this.$page.props.user.roles.includes('admin')"/>
                        </Td>
                    </tr>
                </tbody>
            </Table>
        </template>
    </app-layout>
</template>

<script>
import AppLayout from '@/Layouts/AppLayout.vue'
import PageTitle from '@/Components/PageTitle.vue'
import Table from '@/Components/Table.vue'
import Th from '@/Components/Th.vue'
import Td from '@/Components/Td.vue'
import Button from '@/Components/Button.vue'
import Input from '@/Components/Input.vue'
import Form from '@/Components/Form.vue'
import TableSearch from '@/Components/TableSearch.vue'
import Modal from '@/Components/Modal.vue'
import Select from '@/Components/Select.vue'
import InputGroup from '@/Components/InputGroup.vue'

import store from '@/store'
import { useForm } from '@inertiajs/inertia-vue3'
import { ref } from "vue"
import { Inertia } from "@inertiajs/inertia"

export default {
    components: {
        AppLayout,
        PageTitle,
        Button,
        Table,
        Td,
        Th,
        Input,
        Form,
        TableSearch,
        Modal,
        Select,
        InputGroup,
    },
    props: [
        'webshopProductsGrouped',
        'webshopProducts',
        'assignmentTypes',
        'webshops',
    ],
    setup (props, { attrs })
    {
        let modalTitleAction = ref('toevoegen') //of bewerken
        const modal = ref(null)

        const form = useForm({
            assignment_type_id: null,
            webshop_id: null,
            product_id_nl: null,
            product_id_fr: null,
            product_id_en: null,
            price: null,
            no_webshop_invoice: 0
        });

        function updateWebshopProduct(webshopProduct)
        {
            form.assignment_type_id = webshopProduct.assignment_type_id
            form.webshop_id = webshopProduct.webshop_id
            form.price = webshopProduct.price
            form.no_webshop_invoice = webshopProduct.no_webshop_invoice

            props.webshopProducts.forEach(function(wsProduct, i) {

                if (wsProduct.assignment_type_id === webshopProduct.assignment_type_id && wsProduct.webshop_id === webshopProduct.webshop_id)
                {
                    switch (wsProduct.language)
                    {
                        case 'nl':
                            form.product_id_nl = wsProduct.product_id
                            break;
                        case 'fr':
                            form.product_id_fr = wsProduct.product_id
                            break;
                        case 'en':
                            form.product_id_en = wsProduct.product_id
                            break;
                    }
                }
            })

            modalTitleAction.value = 'bewerken'
            modal.value.show = true
        }

        function deleteWebshopProduct(webshopProduct)
        {
            if (!confirm('Bent u zeker dat u dit webshop product wil verwijderen?'))
                return

            Inertia.delete('/configuration/webshop-products/' + webshopProduct.assignment_type_id + '_' + webshopProduct.webshop_id,
                {
                    data: {
                        assignment_type_id: webshopProduct.assignment_type_id,
                        webshop_id: webshopProduct.webshop_id,
                    }
                })
        }

        function handleSubmit()
        {
            if (modalTitleAction.value === 'toevoegen')
            {
                //Store
                form.post('/configuration/webshop-products', {
                    onSuccess: () => {
                        modal.value.show = false
                        modalClosed()
                    },
                })
            } else {
                //Update
                form.put('/configuration/webshop-products/' + form.assignment_type_id + '_' + form.webshop_id, {
                    onSuccess: () => {
                        modal.value.show = false
                        modalClosed()
                    },
                })
            }
        }

        function modalClosed()
        {
            form.clearErrors()
            form.reset()
            modalTitleAction.value = 'toevoegen'
        }

        return {
            state: store.state,
            ...store,
            modal,
            modalTitleAction,
            form,
            updateWebshopProduct,
            deleteWebshopProduct,
            handleSubmit,
            modalClosed,
        }
    },
    methods: {
        matchesSearch(item, webshopProduct, language) {

            if (item.assignment_type_id === webshopProduct.assignment_type_id && item.webshop_id === webshopProduct.webshop_id
                && item.language === language)
            {
                return true
            }

            return false
        }
    }
}

</script>
